import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { UserDataService } from 'src/sb-tools/typescript/services/user-data.service';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { BsDropdownDirective, BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ComponentMessengerService } from '../../../core/services/component-messenger.service';
import { SignalRService } from 'src/app/core/services/signalR.service';
import { take } from 'rxjs/operators';
import { FolderCountsUpdatedEventModel } from '../../../core/models/dto/signalR/folder-counts-updated-event-model.model';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ShortNumberPipe } from '../../../shared/pipes/short-number.pipe';

@Component({
    selector: 'app-community-switcher',
    templateUrl: './community-switcher.component.html',
    styleUrls: ['./community-switcher.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BsDropdownModule, NgClass, FormsModule, TranslateModule, ShortNumberPipe],
})
export class CommunitySwitcherComponent implements OnInit, OnDestroy {
    @ViewChild('dropdown') private dropdown: BsDropdownDirective;

    communityDisplayName: string;
    filteredCommunitiesCount: number;
    shouldDropdownBeDisplayed: boolean;
    userCommunitiesKeyword: string;
    userCommunities = {
        commonNeedAttentionCount: 0,
        communities: [],
    };

    private unsubscriber = new Subject<void>();

    constructor(
        public commonDataService: CommonDataService,
        private userDataService: UserDataService,
        private componentMessengerService: ComponentMessengerService,
        private title: Title,
        private signalRService: SignalRService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.updateUserCommunities(() => {
            const currentCommunity = this.userCommunities.communities.find((community) => {
                return community.communityName === this.commonDataService.community;
            });

            if (currentCommunity) {
                this.communityDisplayName = currentCommunity.communityDisplayName;
                this.title.setTitle(currentCommunity.communityDisplayName + ' Inbox - Socialboards');
            }

            if (this.userCommunities.communities.length > 1) {
                this.shouldDropdownBeDisplayed = true;

                setInterval(() => {
                    if (this.isTabVisible()) {
                        this.updateUserCommunities();
                    }
                }, 120000);

                this.componentMessengerService.openNewsWidget.pipe(takeUntil(this.unsubscriber)).subscribe((isOpen) => {
                    if (!isOpen) {
                        this.dropdown.hide();
                    }
                });
            } else {
                this.signalRService
                    .onFolderCountersUpdated()
                    .pipe(takeUntil(this.unsubscriber))
                    .subscribe((data: FolderCountsUpdatedEventModel) => {
                        // todo force update (maybe @if)
                        // visual count updated after click on html element
                        if (this.commonDataService.user.isAdmin) {
                            this.userCommunities.commonNeedAttentionCount =
                                data.folderCounts.allTickets.open.totalCount;
                        } else {
                            this.userCommunities.commonNeedAttentionCount = data.folderCounts.myTickets.open.totalCount;
                        }
                    });
            }

            this.changeDetectorRef.detectChanges();
        });
    }

    getFilteredCommunities(): any[] {
        if (this.userCommunitiesKeyword) {
            const keyword = this.userCommunitiesKeyword.toLowerCase();
            const filteredCommunities = this.userCommunities.communities.filter((item) => {
                return item.communityDisplayName.toLowerCase().indexOf(keyword) !== -1;
            });
            const filteredCommunitiesByName = this.userCommunities.communities.filter((item) => {
                return item.communityName.toLowerCase().indexOf(keyword) !== -1;
            });
            filteredCommunitiesByName.forEach((communityByName) => {
                for (const filteredCommunity of filteredCommunities) {
                    if (filteredCommunity.communityId === communityByName.communityId) {
                        return;
                    }
                }
                filteredCommunities.push(communityByName);
            });
            this.filteredCommunitiesCount = filteredCommunities.length;
            return filteredCommunities;
        } else {
            this.filteredCommunitiesCount = this.userCommunities.communities.length;
            return this.userCommunities.communities;
        }
    }

    // todo: check this method, it looks wrong
    isTabVisible(): boolean {
        let stateKey;
        // let eventKey;
        const keys = {
            hidden: 'visibilitychange',
            webkitHidden: 'webkitvisibilitychange',
            mozHidden: 'mozvisibilitychange',
            msHidden: 'msvisibilitychange',
        };

        for (stateKey in keys) {
            if (stateKey in document) {
                // eventKey = keys[stateKey];
                break;
            }
        }

        return !document[stateKey];
    }

    updateUserCommunities(callback?): void {
        this.userDataService
            .getCommunities()
            .pipe(takeUntil(this.unsubscriber), take(1))
            .subscribe((simpleCommunity) => {
                this.shouldDropdownBeDisplayed = this.userCommunities.communities.length > 1;

                this.userCommunities.communities = [...simpleCommunity].sort(
                    (a, b) => b.needAttentionCount - a.needAttentionCount,
                );

                let needAttentionCount = 0;
                simpleCommunity.forEach((community) => {
                    community.link = window.location.origin + '/' + community.communityName + '/tickets';
                    needAttentionCount += community.needAttentionCount;
                });

                this.userCommunities.commonNeedAttentionCount = needAttentionCount;

                if (callback) {
                    setTimeout(() => {
                        callback();
                    }, 100);
                }

                this.changeDetectorRef.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
